import { vm } from "../socket/config.js";
import {
    targetToName,
    nameToTarget,
    setLocalFlag,
} from "../utils/scratch-helpers.js";
import { setPauseEventHandling } from "../vm/overrides.js";

export const onSoundAdded = async (data) => {
    const { soundData, soundName, target } = data.data;
    const t = nameToTarget(target);

    // Was previously checking the raw target *name* string, which is
    // basically always truthy, so a missing/unsynced sprite never got
    // caught here and fell through to using an undefined target below.
    if (!t) {
        console.error("Target not found", target);
        return;
    }

    setLocalFlag(true);

    let asset = soundData.asset;
    const existingAsset = vm.runtime.storage.get(asset.assetId);
    if (!existingAsset) {
        const dataBuffer =
            asset.data instanceof Uint8Array
                ? asset.data
                : Uint8Array.from(asset.data);

        asset = await vm.runtime.storage.createAsset(
            asset.assetType,
            asset.dataFormat,
            dataBuffer,
            asset.assetId,
            true,
        );
    } else asset = existingAsset;

    const soundObj = {
        name: soundData.name || soundName || `sound_${Date.now()}`,
        dataFormat: soundData.dataFormat,
        asset: asset,
        md5: soundData.md5 || `${asset.assetId}.${soundData.dataFormat}`,
        assetId: asset.assetId,
        ...(soundData.rate !== undefined && { rate: soundData.rate }),
        ...(soundData.sampleCount !== undefined && {
            sampleCount: soundData.sampleCount,
        }),
    };

    await withTarget(t, () => vm.addSound(soundObj, t));

    vm.emitTargetsUpdate();
};

export const onSoundDelete = async (data) => {
    const { soundIdx, target } = data.data;
    const t = nameToTarget(target);

    if (!t) {
        console.error("Target not found", target);
        return;
    }

    setPauseEventHandling(true);
    setLocalFlag(true);
    try {
        await withTarget(t, () => vm.deleteSound(soundIdx));
    } catch (e) {
        console.error("Error deleting remote sound:", e);
    } finally {
        setPauseEventHandling(false);
    }
};

const withTarget = async (t, action) => {
    const originalTarget = vm.editingTarget;
    vm.editingTarget = t;

    try {
        await action();
    } finally {
        if (originalTarget && vm.runtime.getTargetById(originalTarget.id)) {
            vm.editingTarget = originalTarget;
            vm.runtime._editingTarget = originalTarget;
        }
    }
};

export const onSoundReorder = (data) => {
    const { soundIndex, newIndex, target } = data.data;
    const t = nameToTarget(target);

    if (!t) {
        console.error("Target not found", target);
        return;
    }

    setLocalFlag(true);
    vm.reorderSound(t.id, soundIndex, newIndex);
    vm.emitTargetsUpdate();
};

export const onSoundRename = async (data) => {
    const { soundIndex, name, target } = data.data;
    const t = nameToTarget(target);

    if (!t) {
        console.error("Target not found", target);
        return;
    }

    await withTarget(t, () => {
        setLocalFlag(true);
        vm.renameSound(soundIndex, name);
    });
};

export const onSoundDuplicate = async (data) => {
    const { soundIndex, name, target } = data.data;
    const t = nameToTarget(target);

    if (!t) {
        console.error("Target not found", target);
        return;
    }

    await withTarget(t, async () => {
        setLocalFlag(true);
        await vm.duplicateSound(soundIndex);

        const copy = t.sprite.sounds[soundIndex + 1];
        if (copy && name) copy.name = name;
    });

    vm.emitTargetsUpdate();
};

export const onSoundUpdate = async (data) => {
    const { soundIndex, sound: soundInfo, assetData, target } = data.data;
    const t = nameToTarget(target);
    const sound = t?.sprite?.sounds?.[soundIndex];

    if (!sound) {
        console.error("Sound not found", target, soundIndex);
        return;
    }

    try {
        const storage = vm.runtime.storage;
        const asset = await storage.createAsset(
            storage.AssetType.Sound,
            soundInfo.dataFormat,
            Uint8Array.from(assetData),
            soundInfo.assetId,
            true,
        );

        Object.assign(sound, soundInfo, { asset, format: "" });

        if (vm.runtime.audioEngine) {
            const player = await vm.runtime.audioEngine.decodeSoundPlayer({
                ...sound,
                data: asset.data,
            });

            sound.soundId = player.id;
            sound.rate = player.buffer.sampleRate;
            sound.sampleCount = player.buffer.length;
            t.sprite.soundBank.addSoundPlayer(player);
        }

        vm.emitTargetsUpdate();
    } catch (e) {
        console.error("Error applying remote sound update:", e);
    }
};
